import React, { ReactElement, useCallback } from 'react';
import { formatTranslation } from 'intl';
import { DocumentationTranslationKeys, CommonTranslationKeys } from 'intl/keys';
import { MemoButtonPlusCreate } from 'components/general/ButtonPlusCreate';
import { MemoPlusIcon } from 'components/core/PlusIcon';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { SxProps, Theme } from '@mui/material';
import { useIntl } from 'react-intl';
import useIsMobile from 'hooks/useIsMobile';
import { Color } from 'shared/utils/colors';
import { openDocumentationInput } from 'shared/domain/documentation/openDocumentationInput';
import { TableAnchorHeader } from 'components/tableAnchor/types';
import { openDirectoryForm } from 'presentation/directory/openDirectoryInput';

const splitGroupSx: SxProps<Theme> = {
  boxShadow: 'none',
  borderRadius: '4px',
  border: `1px solid ${Color.border}`,
  '& .MuiButtonGroup-grouped': {
    backgroundColor: 'transparent',
    color: Color.black,
    textTransform: 'none',
    fontSize: '1rem',
    border: 'none',
    boxShadow: 'none',
    transition: 'background-color 0.15s ease-in-out',
    '&:hover': {
      backgroundColor: Color.primaryOrangeHover,
      border: 'none',
      boxShadow: 'none',
    },
  },
  '& .MuiButtonGroup-grouped:not(:last-of-type)': {
    borderRight: `1px solid ${Color.border}`,
  },
};

const mainButtonSx: SxProps<Theme> = {
  px: 2,
  py: 0.75,
  alignItems: 'center',
  // Collapse the label line box to the glyph height so the text's optical
  // centre lines up with the plus badge (default line-height pushes the
  // text visually above the icon).
  lineHeight: 1,
  '& .MuiButton-startIcon': {
    marginTop: 0,
    marginBottom: 0,
  },
  // Enlarge the plus badge; the circle is transparent and the glyph stays black.
  '& .MuiButton-startIcon > div': {
    height: '26px',
    width: '26px',
    lineHeight: 1,
    backgroundColor: 'transparent',
    color: Color.black,
    '& > .MuiSvgIcon-root': { width: '22px', height: '22px' },
  },
};

const caretButtonSx: SxProps<Theme> = {
  px: 0.5,
  minWidth: '40px !important',
};

// Light, outlined-style dropdown so the extra actions read like the app's
// neutral outlined buttons (dark text on a light surface, brand-yellow hover).
const createMenuPaperSx: SxProps<Theme> = {
  backgroundColor: Color.white,
  color: Color.black,
  '& .MuiMenuItem-root': {
    color: 'inherit',
    '&:hover': {
      backgroundColor: Color.primaryOrangeHover,
    },
  },
  '& .MuiDivider-root': {
    borderColor: Color.border,
  },
};

export function DocumentationCreateMenu(props: {
  headerWithCreation: TableAnchorHeader;
}): ReactElement {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const { headerWithCreation } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      setAnchorEl(event.currentTarget);
    },
    []
  );
  const handleClose = useCallback((): void => {
    setAnchorEl(null);
  }, []);

  const uploadFiles = useCallback(() => {
    openDocumentationInput({ webkitdirectory: false });
    handleClose();
  }, [handleClose]);

  const uploadDirectory = useCallback(() => {
    openDocumentationInput({ webkitdirectory: true });
    handleClose();
  }, [handleClose]);

  const createDirectory = useCallback(() => {
    openDirectoryForm();
    handleClose();
  }, [handleClose]);

  const documentationCreatePermission = useCallback(() => {
    return { permission: true };
  }, []);

  return (
    <div>
      {isMobile ? (
        <MemoButtonPlusCreate
          aria-controls={open ? 'documentation-create-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          creationAction={handleClick}
          canCreate={documentationCreatePermission}
          id={headerWithCreation.id}
          textId={headerWithCreation.textId}
          dataQaId={headerWithCreation.dataQaId}
        />
      ) : (
        <ButtonGroup
          variant='contained'
          color='inherit'
          disableElevation
          sx={splitGroupSx}
          aria-label='documentation create split button'
        >
          <Button
            id={headerWithCreation.id}
            data-qa={headerWithCreation.dataQaId}
            onClick={uploadFiles}
            startIcon={<MemoPlusIcon />}
            sx={mainButtonSx}
          >
            {formatTranslation(intl, DocumentationTranslationKeys.uploadFiles)}
          </Button>
          <Button
            aria-controls={open ? 'documentation-create-menu' : undefined}
            aria-haspopup='menu'
            aria-expanded={open ? 'true' : undefined}
            aria-label={formatTranslation(intl, CommonTranslationKeys.moreOptions)}
            onClick={handleClick}
            sx={caretButtonSx}
          >
            <ArrowDropDownIcon htmlColor={Color.black} />
          </Button>
        </ButtonGroup>
      )}
      <Menu
        id='documentation-create-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: createMenuPaperSx } }}
        MenuListProps={{
          'aria-labelledby': headerWithCreation.id,
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={createDirectory}>
          {formatTranslation(intl, DocumentationTranslationKeys.newDirectory)}
        </MenuItem>
        <Divider />
        <MenuItem onClick={uploadFiles}>
          {formatTranslation(intl, DocumentationTranslationKeys.uploadFiles)}
        </MenuItem>
        <MenuItem onClick={uploadDirectory}>
          {formatTranslation(intl, DocumentationTranslationKeys.uploadDirectory)}
        </MenuItem>
      </Menu>
    </div>
  );
}
